import { useState } from 'react';
import { useDonationBlockProps } from '../DonationBlockProvider';
import { useDonationNavigation } from '../libs/useDonationNavigation';
import { useIPGContext } from './useIPGContext';
import queryString from 'query-string';
import { useDonationStore } from '../libs/useDonationStore';
import { pushDataLayerOnFormStepComplete } from '3-components/Tracker/Tracker';
import { DONATION_FORM_NAME } from '../libs/common';

interface IPGApiResult {
    signedUrl: string | null;
    status: string | null;
}

interface VerifyIPGApiResult {
    success: boolean;
    message: string;
}

interface useIPGPaymentResult {
    isLoading: boolean;
    getIframeURL: (submissionId: string) => Promise<void>;
    verifyIPGPayment: (props: {
            orderId: string;
            orderReference: string;
    }) => Promise<void>;
}

const useIPGPayment = (): useIPGPaymentResult => {
    const [isLoading, setIsLoading] = useState(false);

    const { baseURL, thankYouPageUrl } = useDonationBlockProps();
    const { nextStep } = useDonationNavigation();
    const { setIframeURL: updateIPGIFrameURL } = useIPGContext();
    const { donationData } = useDonationStore();

    const getIframeURL = async(submissionId: string): Promise<void> => {
        // set loading status
        setIsLoading(true);

        try {
            const formdata = new FormData();
            formdata.append('submissionId', submissionId);

            const request = await fetch(`${baseURL}/api/ipgpayment/signedurl`, {
                method: 'POST',
                body: formdata,
            });

            if (!request.ok) {
                throw new Error('Failed to get IPG payment url');
            }

            const result: IPGApiResult = await request.json();

            if (!result.signedUrl) {
                throw new Error('Failed to generate payment url');
            }

            // update ipg payment data
            updateIPGIFrameURL(result.signedUrl);
            // console.info('iframe url: ', result.signedUrl);

            // go to the next step
            nextStep();
        } catch (error) {
            console.error('Fetching ipg payment failed', error);
            throw error;
        }

        // reset loading status
        setIsLoading(false);
    };

    const verifyIPGPayment: useIPGPaymentResult['verifyIPGPayment'] = async props => {
        setIsLoading(true);

        const { orderId, orderReference } = props;

        const formdata = new FormData();
        formdata.append('OrderId', orderId);
        formdata.append('OrderReference', orderReference);

        try {
            const checkingPayment = await fetch(`${baseURL}/api/ipgpaymenthandler`, {
                method: 'POST',
                body: formdata,
            });

            if (!checkingPayment.ok) {
                throw new Error('[IPG] Failed to verify payment');
            }

            const result: VerifyIPGApiResult = await checkingPayment.json();

            if (!result.success) {
                throw new Error('[IPG] Payment is not verified');
            }

            if (thankYouPageUrl) {
                // GA4 dataLayer => step complete
                pushDataLayerOnFormStepComplete({
                    formName: DONATION_FORM_NAME,
                    formType: 'donation',
                    stepName: 'payment',
                });

                const thankYouUrl = queryString.stringifyUrl({
                    url: thankYouPageUrl,
                    query: { donationKey: donationData.submissionKey },
                });
                // redirect to thank you page
                window.location.href = thankYouUrl;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }

        setIsLoading(false);
    };

    return { isLoading, getIframeURL, verifyIPGPayment };
};

export default useIPGPayment;
